
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useUserStore } from '../store/userStore';
import { BADGES } from '../data/badges';
import { LESSON_CONTENT } from '../data/lessonContent';
import { Card } from '../components/common/Card';
import { Page } from '../components/layout/Page';

const LEVEL_TITLES = {
  1: 'مستكشف مبتدئ',
  2: 'مستكشف نشيط',
  3: 'عالم جغرافيا صغير',
  4: 'خبير كنوز مصر',
  5: 'قائد التحديات'
};

const TRUE_FALSE_BANK = [
  {
    id: 'tf-1',
    statement: 'منجم السكري من أهم مناجم الذهب في مصر.',
    answer: true,
    explanation: 'صحيح، منجم السكري من أكبر وأشهر مناجم الذهب في مصر.'
  },
  {
    id: 'tf-2',
    statement: 'الطاقة الشمسية مصدر غير متجدد.',
    answer: false,
    explanation: 'خطأ، الطاقة الشمسية مصدر متجدد وصديق للبيئة.'
  },
  {
    id: 'tf-3',
    statement: 'الري بالتنقيط يساعد على توفير المياه.',
    answer: true,
    explanation: 'صحيح، لأنه يوصل الماء مباشرة للجذور ويقلل الهدر.'
  },
  {
    id: 'tf-4',
    statement: 'نهر النيل ليس له دور في الزراعة بمصر.',
    answer: false,
    explanation: 'خطأ، النيل المصدر الرئيسي للمياه والزراعة.'
  },
  {
    id: 'tf-5',
    statement: 'الفوسفات يدخل في صناعة الأسمدة.',
    answer: true,
    explanation: 'صحيح، وهو عنصر أساسي في إنتاج الأسمدة.'
  },
  {
    id: 'tf-6',
    statement: 'إعادة تدوير المعادن تضر البيئة دائمًا.',
    answer: false,
    explanation: 'خطأ، إعادة التدوير غالبًا تقلل التلوث واستهلاك الموارد.'
  },
  {
    id: 'tf-7',
    statement: 'القطن المصري معروف بجودته عالميًا.',
    answer: true,
    explanation: 'صحيح، القطن المصري مشهور بجودة أليافه.'
  },
  {
    id: 'tf-8',
    statement: 'المياه الجوفية توجد فوق سطح الأرض.',
    answer: false,
    explanation: 'خطأ، المياه الجوفية مخزنة تحت سطح الأرض.'
  },
  {
    id: 'tf-9',
    statement: 'الرياح يمكن استخدامها لتوليد الكهرباء.',
    answer: true,
    explanation: 'صحيح، طاقة الرياح من مصادر الطاقة المتجددة.'
  },
  {
    id: 'tf-10',
    statement: 'كل الموارد الطبيعية تتجدد بسرعة.',
    answer: false,
    explanation: 'خطأ، بعض الموارد مثل المعادن غير متجددة.'
  }
];

const FILL_BLANK_BANK = [
  {
    id: 'fill-1',
    sentence: 'من أشهر مناجم الذهب في مصر منجم _____.',
    accepted: ['السكري']
  },
  {
    id: 'fill-2',
    sentence: 'يدخل _____ في صناعة الأسمدة الزراعية.',
    accepted: ['الفوسفات']
  },
  {
    id: 'fill-3',
    sentence: 'نهر _____ هو شريان الحياة في مصر.',
    accepted: ['النيل']
  },
  {
    id: 'fill-4',
    sentence: 'الطاقة _____ مصدر متجدد ونظيف.',
    accepted: ['الشمسية']
  },
  {
    id: 'fill-5',
    sentence: 'الري _____ يساعد على تقليل هدر المياه.',
    accepted: ['بالتنقيط', 'تنقيط']
  },
  {
    id: 'fill-6',
    sentence: 'توجد المياه الجوفية _____ سطح الأرض.',
    accepted: ['تحت']
  },
  {
    id: 'fill-7',
    sentence: 'المورد غير المتجدد يحتاج ملايين _____ ليتكون.',
    accepted: ['السنين', 'السنوات']
  },
  {
    id: 'fill-8',
    sentence: 'من المحاصيل المشهورة في مصر محصول _____.',
    accepted: ['القمح', 'القطن', 'الأرز']
  },
  {
    id: 'fill-9',
    sentence: 'إعادة _____ تحافظ على البيئة وتوفر الموارد.',
    accepted: ['التدوير']
  },
  {
    id: 'fill-10',
    sentence: 'النحاس معدن ممتاز لتوصيل _____.',
    accepted: ['الكهرباء']
  },
  {
    id: 'fill-11',
    sentence: 'الطاقة _____ من أشهر مصادر الطاقة المتجددة في السواحل.',
    accepted: ['الرياح']
  },
  {
    id: 'fill-12',
    sentence: 'من صور الحفاظ على الماء غلق _____ بعد الاستخدام.',
    accepted: ['الصنبور']
  }
];

const ORDERING_CHALLENGES = [
  {
    id: 'order-1',
    title: 'رتب مراحل استخراج المعدن',
    steps: ['استكشاف الموقع', 'استخراج الخام', 'تنقية الخام', 'استخدام المعدن في الصناعة']
  },
  {
    id: 'order-2',
    title: 'رتب خطوات الحفاظ على المياه',
    steps: ['اكتشاف الهدر', 'إصلاح التسريب', 'استخدام طرق ري حديثة', 'متابعة الاستهلاك يوميًا']
  },
  {
    id: 'order-3',
    title: 'رتب رحلة إنتاج الطاقة النظيفة',
    steps: ['التقاط ضوء الشمس أو الرياح', 'تحويلها لطاقة كهربائية', 'تخزين أو نقل الطاقة', 'استخدام الكهرباء في المنازل']
  },
  {
    id: 'order-4',
    title: 'رتب خطوات التعلم الذكي',
    steps: ['قراءة الدرس', 'حل الأسئلة', 'تطبيق نشاط عملي', 'مراجعة الأخطاء والتصحيح']
  }
];

const MEMORY_SYMBOLS = ['🪨', '⚡', '🌾', '💧', '🗺️', '☀️', '🌍', '🔬'];

const shuffle = (items) => {
  const cloned = [...items];
  for (let i = cloned.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
};

const normalizeAnswer = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[ًٌٍَُِّْـ]/g, '')
    .replace(/[اأإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/\s+/g, ' ');

const createMemoryDeck = () =>
  shuffle(
    MEMORY_SYMBOLS.flatMap((symbol, index) => [
      { id: `${index}-a`, pairId: symbol, symbol },
      { id: `${index}-b`, pairId: symbol, symbol }
    ])
  );

const buildMcqBank = () =>
  Object.entries(LESSON_CONTENT).flatMap(([lessonId, lesson]) =>
    (lesson?.questions?.mcq || []).map((question, index) => ({
      id: `${lessonId}-mcq-${index}`,
      lessonId,
      question
    }))
  );

const buildShortBank = () =>
  Object.entries(LESSON_CONTENT).flatMap(([lessonId, lesson]) =>
    (lesson?.questions?.short || []).map((question, index) => ({
      id: `${lessonId}-short-${index}`,
      lessonId,
      question
    }))
  );

const buildActivityBank = () =>
  Object.entries(LESSON_CONTENT).flatMap(([lessonId, lesson]) =>
    (lesson?.activities || []).map((activity, index) => ({
      id: `${lessonId}-activity-${index}`,
      lessonId,
      activity
    }))
  );

const buildGlossaryBank = () =>
  Object.entries(LESSON_CONTENT).flatMap(([lessonId, lesson]) =>
    (lesson?.glossary || []).map((item, index) => ({
      id: `${lessonId}-glossary-${index}`,
      term: item.term,
      definition: item.definition
    }))
  );

const randomTapPosition = () => ({
  x: Math.floor(Math.random() * 70) + 8,
  y: Math.floor(Math.random() * 58) + 8
});

export const Awards = () => {
  const {
    points,
    level,
    streak,
    mapInteractions,
    gamesWon,
    completedLessons,
    earnedBadges,
    addPoints,
    earnBadge,
    incrementGamesWon
  } = useUserStore();

  const levelTitle = LEVEL_TITLES[level] || 'أسطورة كنوز مصر';
  const levelBase = Math.floor(points / 50) * 50;
  const nextLevelAt = levelBase + 50;
  const progressPct = Math.min(100, Math.round(((points - levelBase) / 50) * 100));
  const remainingToNext = Math.max(0, nextLevelAt - points);

  const userSnapshot = useMemo(
    () => ({ completedLessons, mapInteractions, gamesWon, streak }),
    [completedLessons, mapInteractions, gamesWon, streak]
  );

  useEffect(() => {
    BADGES.forEach((badge) => {
      if (!earnedBadges.includes(badge.id) && badge.condition(userSnapshot)) {
        earnBadge(badge.id);
      }
    });
  }, [earnedBadges, earnBadge, userSnapshot]);

  const mcqBank = useMemo(() => buildMcqBank(), []);
  const shortBank = useMemo(() => buildShortBank(), []);
  const activityBank = useMemo(() => buildActivityBank(), []);
  const glossaryBank = useMemo(() => buildGlossaryBank(), []);

  const [mcqRound, setMcqRound] = useState(0);
  const [tfRound, setTfRound] = useState(0);
  const [fillRound, setFillRound] = useState(0);
  const [matchingRound, setMatchingRound] = useState(0);
  const [shortRound, setShortRound] = useState(0);
  const [activityRound, setActivityRound] = useState(0);

  const [mcqAnswers, setMcqAnswers] = useState({});
  const [tfAnswers, setTfAnswers] = useState({});
  const [fillInputs, setFillInputs] = useState({});
  const [fillResults, setFillResults] = useState({});
  const [matchingAnswers, setMatchingAnswers] = useState({});
  const [revealedShortAnswers, setRevealedShortAnswers] = useState({});
  const [doneActivities, setDoneActivities] = useState({});

  const [mcqRewardClaimed, setMcqRewardClaimed] = useState(false);
  const [tfRewardClaimed, setTfRewardClaimed] = useState(false);
  const [fillRewardClaimed, setFillRewardClaimed] = useState(false);
  const [matchingRewardClaimed, setMatchingRewardClaimed] = useState(false);
  const [shortRewardClaimed, setShortRewardClaimed] = useState(false);
  const [activityRewardClaimed, setActivityRewardClaimed] = useState(false);

  const [memoryDeck, setMemoryDeck] = useState(() => createMemoryDeck());
  const [flippedMemory, setFlippedMemory] = useState([]);
  const [matchedMemoryPairs, setMatchedMemoryPairs] = useState([]);
  const [memoryMoves, setMemoryMoves] = useState(0);
  const [memoryWon, setMemoryWon] = useState(false);
  const [memoryRewardClaimed, setMemoryRewardClaimed] = useState(false);

  const [tapRunning, setTapRunning] = useState(false);
  const [tapTimeLeft, setTapTimeLeft] = useState(20);
  const [tapScore, setTapScore] = useState(0);
  const [tapBest, setTapBest] = useState(0);
  const [tapPosition, setTapPosition] = useState({ x: 40, y: 40 });
  const [tapWon, setTapWon] = useState(false);
  const [tapRewardClaimed, setTapRewardClaimed] = useState(false);

  const [orderRound, setOrderRound] = useState(0);
  const [orderItems, setOrderItems] = useState([]);
  const [orderWon, setOrderWon] = useState(false);
  const [orderRewardClaimed, setOrderRewardClaimed] = useState(false);

  const mcqQuestions = useMemo(() => shuffle(mcqBank).slice(0, 14), [mcqBank, mcqRound]);
  const tfQuestions = useMemo(() => shuffle(TRUE_FALSE_BANK).slice(0, 10), [tfRound]);
  const fillQuestions = useMemo(() => shuffle(FILL_BLANK_BANK).slice(0, 10), [fillRound]);
  const shortQuestions = useMemo(() => shuffle(shortBank).slice(0, 12), [shortBank, shortRound]);
  const activityChallenges = useMemo(
    () => shuffle(activityBank).slice(0, 10),
    [activityBank, activityRound]
  );

  const matchingPairs = useMemo(
    () => shuffle(glossaryBank).slice(0, Math.min(8, glossaryBank.length)),
    [glossaryBank, matchingRound]
  );

  const matchingDefinitions = useMemo(
    () => shuffle(matchingPairs.map((item) => ({ id: item.id, definition: item.definition }))),
    [matchingPairs]
  );

  const currentOrderChallenge = useMemo(() => shuffle(ORDERING_CHALLENGES)[0], [orderRound]);

  useEffect(() => {
    const prepared = currentOrderChallenge.steps.map((step, index) => ({
      id: `${currentOrderChallenge.id}-${index}`,
      text: step,
      correctIndex: index
    }));

    setOrderItems(shuffle(prepared));
    setOrderWon(false);
    setOrderRewardClaimed(false);
  }, [currentOrderChallenge]);

  const mcqAnsweredCount = Object.keys(mcqAnswers).length;
  const mcqScore = mcqQuestions.reduce((score, item) => {
    const selected = mcqAnswers[item.id];
    return selected === item.question.answer ? score + 1 : score;
  }, 0);

  const tfAnsweredCount = Object.keys(tfAnswers).length;
  const tfScore = tfQuestions.reduce((score, item) => {
    const selected = tfAnswers[item.id];
    return selected === item.answer ? score + 1 : score;
  }, 0);

  const fillCheckedCount = Object.keys(fillResults).length;
  const fillScore = fillQuestions.reduce((score, item) => {
    return fillResults[item.id] ? score + 1 : score;
  }, 0);

  const matchingAnsweredCount = Object.keys(matchingAnswers).length;
  const matchingScore = matchingPairs.reduce((score, item) => {
    return matchingAnswers[item.id] === item.id ? score + 1 : score;
  }, 0);

  const shortSolvedCount = Object.keys(revealedShortAnswers).length;
  const activityDoneCount = Object.values(doneActivities).filter(Boolean).length;

  const memoryCompleted = matchedMemoryPairs.length === MEMORY_SYMBOLS.length;
  const orderSolved =
    orderItems.length > 0 &&
    orderItems.every((item, index) => item.correctIndex === index);

  const totalChallengesCount =
    mcqBank.length +
    shortBank.length +
    activityBank.length +
    TRUE_FALSE_BANK.length +
    FILL_BLANK_BANK.length +
    glossaryBank.length +
    ORDERING_CHALLENGES.length +
    MEMORY_SYMBOLS.length * 2;

  useEffect(() => {
    if (flippedMemory.length !== 2) {
      return undefined;
    }

    const [firstIndex, secondIndex] = flippedMemory;
    const firstCard = memoryDeck[firstIndex];
    const secondCard = memoryDeck[secondIndex];

    setMemoryMoves((prev) => prev + 1);

    if (firstCard.pairId === secondCard.pairId) {
      setMatchedMemoryPairs((prev) =>
        prev.includes(firstCard.pairId) ? prev : [...prev, firstCard.pairId]
      );
      setFlippedMemory([]);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setFlippedMemory([]);
    }, 650);

    return () => clearTimeout(timeoutId);
  }, [flippedMemory, memoryDeck]);

  useEffect(() => {
    if (memoryCompleted && !memoryWon) {
      setMemoryWon(true);
      incrementGamesWon();
    }
  }, [memoryCompleted, memoryWon, incrementGamesWon]);

  useEffect(() => {
    if (!tapRunning) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setTapTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          setTapRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [tapRunning]);

  useEffect(() => {
    if (tapRunning || tapTimeLeft !== 0) {
      return;
    }

    setTapBest((prev) => Math.max(prev, tapScore));

    if (tapScore >= 12 && !tapWon) {
      setTapWon(true);
      incrementGamesWon();
    }
  }, [tapRunning, tapTimeLeft, tapScore, tapWon, incrementGamesWon]);

  useEffect(() => {
    if (orderSolved && !orderWon) {
      setOrderWon(true);
      incrementGamesWon();
    }
  }, [orderSolved, orderWon, incrementGamesWon]);

  const resetMcqRound = () => {
    setMcqRound((prev) => prev + 1);
    setMcqAnswers({});
    setMcqRewardClaimed(false);
  };

  const resetTfRound = () => {
    setTfRound((prev) => prev + 1);
    setTfAnswers({});
    setTfRewardClaimed(false);
  };

  const resetFillRound = () => {
    setFillRound((prev) => prev + 1);
    setFillInputs({});
    setFillResults({});
    setFillRewardClaimed(false);
  };

  const resetMatchingRound = () => {
    setMatchingRound((prev) => prev + 1);
    setMatchingAnswers({});
    setMatchingRewardClaimed(false);
  };

  const resetShortRound = () => {
    setShortRound((prev) => prev + 1);
    setRevealedShortAnswers({});
    setShortRewardClaimed(false);
  };

  const resetActivityRound = () => {
    setActivityRound((prev) => prev + 1);
    setDoneActivities({});
    setActivityRewardClaimed(false);
  };

  const resetMemoryGame = () => {
    setMemoryDeck(createMemoryDeck());
    setFlippedMemory([]);
    setMatchedMemoryPairs([]);
    setMemoryMoves(0);
    setMemoryWon(false);
    setMemoryRewardClaimed(false);
  };

  const startTapGame = () => {
    setTapRunning(true);
    setTapTimeLeft(20);
    setTapScore(0);
    setTapWon(false);
    setTapRewardClaimed(false);
    setTapPosition(randomTapPosition());
  };

  const handleFillCheck = (question) => {
    const inputValue = normalizeAnswer(fillInputs[question.id] || '');
    const isCorrect = question.accepted.some(
      (answer) => normalizeAnswer(answer) === inputValue
    );

    setFillResults((prev) => ({
      ...prev,
      [question.id]: isCorrect
    }));
  };

  const handleMemoryClick = (index) => {
    if (flippedMemory.length >= 2) {
      return;
    }

    const card = memoryDeck[index];
    if (!card) {
      return;
    }

    if (flippedMemory.includes(index) || matchedMemoryPairs.includes(card.pairId)) {
      return;
    }

    setFlippedMemory((prev) => [...prev, index]);
  };

  const handleTapTargetClick = () => {
    if (!tapRunning) {
      return;
    }

    setTapScore((prev) => prev + 1);
    setTapPosition(randomTapPosition());
  };

  const moveOrderItem = (index, direction) => {
    setOrderItems((prev) => {
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= prev.length) {
        return prev;
      }

      const cloned = [...prev];
      [cloned[index], cloned[nextIndex]] = [cloned[nextIndex], cloned[index]];
      return cloned;
    });
  };

  return (
    <Page className="space-y-8">
      <section className="glass-panel p-7 md:p-10 relative overflow-hidden">
        <div className="absolute -top-28 -right-20 w-72 h-72 rounded-full bg-palmGreen/20 blur-3xl" />
        <div className="absolute -bottom-28 -left-24 w-80 h-80 rounded-full bg-nileBlue/20 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[460px] h-[460px] rounded-full bg-gold/10 blur-3xl" />

        <div className="relative space-y-3">
          <h1 className="text-4xl md:text-5xl font-black text-gray-900">🏆 أكاديمية الجوائز والتحديات</h1>
          <p className="text-gray-700 max-w-3xl">
            صفحة مليانة أسئلة متنوعة وألعاب أطفال تعليمية. جاوب، العب، واجمع نقاط أكثر وارفع مستواك في رحلة كنوز مصر.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="chip chip-strong">المستوى {level}</span>
            <span className="chip">الشارات {earnedBadges.length}/{BADGES.length}</span>
            <span className="chip">الدروس المكتملة {completedLessons.length}/3</span>
            <span className="chip">🔥 سلسلة التعلم {streak}</span>
            <span className="chip">🎮 انتصارات الألعاب {gamesWon}</span>
          </div>
        </div>
      </section>

      <Card className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-black text-gray-900">{levelTitle}</h2>
            <p className="text-gray-700">المستوى الحالي: <span className="number font-black">{level}</span></p>
          </div>
          <div className="text-4xl">{level >= 5 ? '👑' : '🎯'}</div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>التقدم للمستوى التالي</span>
            <span className="number font-black">{points} / {nextLevelAt}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 rounded-full"
              style={{
                width: `${progressPct}%`,
                background: 'linear-gradient(90deg, #1E88E5 0%, #FFD54F 55%, #66BB6A 110%)'
              }}
            />
          </div>
          <p className="text-xs text-gray-600">متبقي {remainingToNext} نقطة للوصول للمستوى التالي.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">{points}</p>
            <p className="text-xs text-gray-600">النقاط الحالية</p>
          </div>
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">{mapInteractions}</p>
            <p className="text-xs text-gray-600">تفاعل بالخريطة</p>
          </div>
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">{gamesWon}</p>
            <p className="text-xs text-gray-600">ألعاب فزت بها</p>
          </div>
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">{streak}</p>
            <p className="text-xs text-gray-600">أيام متتالية</p>
          </div>
        </div>
      </Card>

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-2xl font-black text-gray-900">بنك الأسئلة والمهام</h2>
          <span className="chip chip-strong">{totalChallengesCount} تحدي متنوع</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">اختيار من متعدد</h3>
              <span className="chip">النتيجة: {mcqScore}/{mcqQuestions.length}</span>
            </div>

            <div className="space-y-3 max-h-[470px] overflow-y-auto pr-1">
              {mcqQuestions.map((item, index) => {
                const selected = mcqAnswers[item.id];
                const answered = selected !== undefined;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                    className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2"
                  >
                    <p className="font-semibold text-gray-900">س{index + 1}: {item.question.q}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {item.question.options.map((option, optionIndex) => {
                        const isCorrectOption = optionIndex === item.question.answer;
                        const isSelectedOption = selected === optionIndex;
                        const optionClass = answered
                          ? isCorrectOption
                            ? 'border-palmGreen bg-palmGreen/10 text-palmGreen'
                            : isSelectedOption
                              ? 'border-red-300 bg-red-50 text-red-700'
                              : 'border-gray-200 bg-white'
                          : 'border-gray-200 bg-white hover:border-cyan-300';

                        return (
                          <button
                            key={`${item.id}-${option}`}
                            type="button"
                            disabled={answered}
                            onClick={() => setMcqAnswers((prev) => ({ ...prev, [item.id]: optionIndex }))}
                            className={`text-right rounded-xl border px-3 py-2 text-sm transition ${optionClass}`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                    {answered && <p className="text-xs text-gray-600">التفسير: {item.question.explanation}</p>}
                  </motion.div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetMcqRound}>مجموعة جديدة</button>
              {mcqAnsweredCount === mcqQuestions.length && !mcqRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(mcqScore >= 11 ? 30 : mcqScore >= 8 ? 22 : 12);
                    setMcqRewardClaimed(true);
                  }}
                >
                  استلام مكافأة الاختبار
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">تحدي صح أم خطأ</h3>
              <span className="chip">النتيجة: {tfScore}/{tfQuestions.length}</span>
            </div>

            <div className="space-y-3 max-h-[470px] overflow-y-auto pr-1">
              {tfQuestions.map((item, index) => {
                const selected = tfAnswers[item.id];
                const answered = selected !== undefined;

                return (
                  <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                    <p className="font-semibold text-gray-900">تحدي {index + 1}: {item.statement}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={answered}
                        onClick={() => setTfAnswers((prev) => ({ ...prev, [item.id]: true }))}
                        className={`chip ${answered && selected === true && item.answer ? 'chip-strong' : ''}`}
                      >
                        صح
                      </button>
                      <button
                        type="button"
                        disabled={answered}
                        onClick={() => setTfAnswers((prev) => ({ ...prev, [item.id]: false }))}
                        className={`chip ${answered && selected === false && !item.answer ? 'chip-strong' : ''}`}
                      >
                        خطأ
                      </button>
                    </div>
                    {answered && <p className="text-xs text-gray-600">{item.explanation}</p>}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetTfRound}>تحدي جديد</button>
              {tfAnsweredCount === tfQuestions.length && !tfRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(tfScore >= 8 ? 20 : tfScore >= 6 ? 14 : 8);
                    setTfRewardClaimed(true);
                  }}
                >
                  استلام مكافأة التحدي
                </button>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">أكمل الفراغ</h3>
              <span className="chip">النتيجة: {fillScore}/{fillQuestions.length}</span>
            </div>

            <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
              {fillQuestions.map((item, index) => {
                const result = fillResults[item.id];
                return (
                  <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                    <p className="font-semibold text-gray-900">{index + 1}. {item.sentence}</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={fillInputs[item.id] || ''}
                        onChange={(event) =>
                          setFillInputs((prev) => ({ ...prev, [item.id]: event.target.value }))
                        }
                        className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
                        placeholder="اكتب الإجابة هنا"
                      />
                      <button
                        type="button"
                        className="chip"
                        onClick={() => handleFillCheck(item)}
                      >
                        تحقق
                      </button>
                    </div>
                    {result !== undefined && (
                      <p className={`text-xs ${result ? 'text-palmGreen' : 'text-red-600'}`}>
                        {result
                          ? 'إجابة ممتازة!'
                          : `الإجابة الأقرب: ${item.accepted[0]}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetFillRound}>مجموعة جديدة</button>
              {fillCheckedCount === fillQuestions.length && !fillRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(fillScore >= 8 ? 18 : 10);
                    setFillRewardClaimed(true);
                  }}
                >
                  استلام مكافأة الإكمال
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">لعبة التوصيل</h3>
              <span className="chip">الصحيح: {matchingScore}/{matchingPairs.length}</span>
            </div>

            <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
              {matchingPairs.map((item, index) => (
                <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                  <p className="font-semibold text-gray-900">{index + 1}. {item.term}</p>
                  <select
                    value={matchingAnswers[item.id] || ''}
                    onChange={(event) =>
                      setMatchingAnswers((prev) => ({ ...prev, [item.id]: event.target.value }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="">اختر التعريف المناسب</option>
                    {matchingDefinitions.map((definition) => (
                      <option key={`${item.id}-${definition.id}`} value={definition.id}>
                        {definition.definition}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetMatchingRound}>إعادة التحدي</button>
              {matchingAnsweredCount === matchingPairs.length && !matchingRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(matchingScore >= 6 ? 18 : 10);
                    setMatchingRewardClaimed(true);
                  }}
                >
                  استلام مكافأة التوصيل
                </button>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">أسئلة مفتوحة للتفكير</h3>
              <span className="chip">تمت المراجعة {shortSolvedCount}/{shortQuestions.length}</span>
            </div>

            <div className="space-y-2 max-h-[410px] overflow-y-auto pr-1">
              {shortQuestions.map((item, index) => {
                const isRevealed = Boolean(revealedShortAnswers[item.id]);
                return (
                  <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                    <p className="font-semibold text-gray-900">سؤال {index + 1}: {item.question.q}</p>
                    {isRevealed ? (
                      <p className="text-sm text-gray-700">إجابة مقترحة: {item.question.answer}</p>
                    ) : (
                      <button
                        type="button"
                        className="chip"
                        onClick={() =>
                          setRevealedShortAnswers((prev) => ({ ...prev, [item.id]: true }))
                        }
                      >
                        إظهار الإجابة المقترحة
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetShortRound}>أسئلة جديدة</button>
              {shortSolvedCount >= 8 && !shortRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(14);
                    setShortRewardClaimed(true);
                  }}
                >
                  استلام مكافأة التفكير
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">تحديات عملية</h3>
              <span className="chip">أنجزت {activityDoneCount}/{activityChallenges.length}</span>
            </div>

            <div className="space-y-2 max-h-[410px] overflow-y-auto pr-1">
              {activityChallenges.map((item, index) => {
                const isDone = Boolean(doneActivities[item.id]);
                return (
                  <label
                    key={item.id}
                    className={`flex items-start gap-3 rounded-2xl border p-3 transition ${
                      isDone ? 'border-palmGreen bg-palmGreen/10' : 'border-white/40 bg-white/70'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isDone}
                      onChange={() =>
                        setDoneActivities((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                      }
                      className="w-4 h-4 mt-1"
                    />
                    <p className="text-sm text-gray-800">مهمة {index + 1}: {item.activity}</p>
                  </label>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetActivityRound}>مهام جديدة</button>
              {activityDoneCount >= 6 && !activityRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(20);
                    setActivityRewardClaimed(true);
                  }}
                >
                  استلام مكافأة المهام
                </button>
              )}
            </div>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-black text-gray-900">🎮 ألعاب أطفال تعليمية</h2>
          <span className="chip chip-strong">كل لعبة تساعدك تكسب نقاط وتفتح شارات</span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-gray-900">لعبة الذاكرة</h3>
              <span className="chip">الحركات: {memoryMoves}</span>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {memoryDeck.map((card, index) => {
                const isFlipped = flippedMemory.includes(index) || matchedMemoryPairs.includes(card.pairId);
                return (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => handleMemoryClick(index)}
                    className={`h-14 rounded-xl border text-2xl transition ${
                      isFlipped
                        ? 'border-gold bg-gold/20'
                        : 'border-gray-200 bg-white hover:border-cyan-300'
                    }`}
                  >
                    {isFlipped ? card.symbol : '❓'}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetMemoryGame}>إعادة اللعبة</button>
              {memoryCompleted && !memoryRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(memoryMoves <= 10 ? 30 : 20);
                    setMemoryRewardClaimed(true);
                  }}
                >
                  استلام مكافأة الذاكرة
                </button>
              )}
            </div>

            {memoryCompleted && (
              <p className="text-xs text-palmGreen font-semibold">ممتاز! خلصت لعبة الذاكرة بنجاح.</p>
            )}
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-gray-900">لعبة السرعة</h3>
              <span className="chip">أفضل نتيجة: {tapBest}</span>
            </div>

            <div className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-3">
              <div className="flex items-center justify-between text-sm text-gray-700">
                <span>الوقت: {tapTimeLeft} ثانية</span>
                <span>النقاط: {tapScore}</span>
              </div>

              <div className="relative h-52 rounded-2xl bg-gradient-to-b from-sky-100 to-yellow-100 overflow-hidden border border-white/60">
                <motion.button
                  type="button"
                  onClick={handleTapTargetClick}
                  disabled={!tapRunning}
                  whileTap={{ scale: 0.92 }}
                  className="absolute text-3xl w-14 h-14 rounded-full bg-white/90 border border-gold shadow-sm flex items-center justify-center disabled:opacity-50"
                  style={{ left: `${tapPosition.x}%`, top: `${tapPosition.y}%`, transform: 'translate(-50%, -50%)' }}
                >
                  ⭐
                </motion.button>
              </div>

              <div className="flex flex-wrap gap-2">
                {!tapRunning ? (
                  <button type="button" className="chip" onClick={startTapGame}>ابدأ 20 ثانية</button>
                ) : (
                  <span className="chip chip-strong">اضغط النجمة بسرعة</span>
                )}

                {!tapRunning && tapTimeLeft === 0 && tapScore >= 12 && !tapRewardClaimed && (
                  <button
                    type="button"
                    className="chip chip-strong"
                    onClick={() => {
                      addPoints(tapScore >= 18 ? 26 : 16);
                      setTapRewardClaimed(true);
                    }}
                  >
                    استلام مكافأة السرعة
                  </button>
                )}
              </div>

              {!tapRunning && tapTimeLeft === 0 && (
                <p className={`text-xs font-semibold ${tapWon ? 'text-palmGreen' : 'text-gray-600'}`}>
                  {tapWon
                    ? 'نتيجة رائعة! فزت بالتحدي السريع.'
                    : 'جرب مرة أخرى وحاول توصل 12 نقطة أو أكثر.'}
                </p>
              )}
            </div>
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-black text-gray-900">لعبة ترتيب الخطوات</h3>
              <span className="chip">{currentOrderChallenge.title}</span>
            </div>

            <div className="space-y-2">
              {orderItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`rounded-xl border p-2 flex items-center gap-2 ${
                    orderSolved ? 'border-palmGreen bg-palmGreen/10' : 'border-white/40 bg-white/70'
                  }`}
                >
                  <span className="chip number">{index + 1}</span>
                  <p className="text-sm text-gray-800 flex-1">{item.text}</p>
                  <div className="flex gap-1">
                    <button type="button" className="chip" onClick={() => moveOrderItem(index, 'up')}>⬆️</button>
                    <button type="button" className="chip" onClick={() => moveOrderItem(index, 'down')}>⬇️</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={() => setOrderRound((prev) => prev + 1)}>
                تحدي ترتيب جديد
              </button>
              {orderSolved && !orderRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(18);
                    setOrderRewardClaimed(true);
                  }}
                >
                  استلام مكافأة الترتيب
                </button>
              )}
            </div>

            {orderSolved && <p className="text-xs text-palmGreen font-semibold">ترتيب ممتاز! 👏</p>}
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-black text-gray-900">الشارات</h2>
          <span className="chip">افتح المزيد بالاستكشاف والتحديات</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {BADGES.map((badge, index) => {
            const isEarned = earnedBadges.includes(badge.id);
            return (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.03, duration: 0.25 }}
                className={isEarned ? '' : 'opacity-60 grayscale'}
              >
                <div className="glass-panel p-5 rounded-3xl text-center space-y-2">
                  <div className="text-5xl">{badge.icon}</div>
                  <p className="font-black text-gray-900">{badge.name}</p>
                  <p className="text-xs text-gray-700">{badge.description}</p>
                  {!isEarned && <span className="chip">🔒 مقفل</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </Page>
  );
};
